import React from 'react';
import { Image } from '@yme/atoms/image';
import { VideoPlayer } from '@yme/atoms/video-player';
import { View360 } from '@yme/components/View360';
import { getDimension } from '@yme/hooks/useRenditions';
import { buildYoutubeUrl, getImageUrlByDimension, getYoutubePreview } from '@yme/utils/common';
import { ACTION_IMAGE, DEGREES_360, GalleryItemType, STATIC_IMAGE, STUDIO_IMAGE, } from '@yme/utils/constants';
import { ACCESSORY_KEY, VIDEO, VIEW360 } from './constants';
const moveGeneralImageToStart = (imagesArrays, type) => imagesArrays.map((galleryImages) => {
    const imageId = galleryImages.find(({ type: itemType }) => itemType === type)?.id ?? '';
    return galleryImages.sort((a, b) => {
        if (a.id === imageId) {
            return -1;
        }
        return b.id === imageId ? 1 : 0;
    });
});
export const take360DegreesImages = (allImages) => allImages?.filter((image) => {
    const lowerCaseUrl = image.url.toLowerCase();
    return lowerCaseUrl.indexOf(DEGREES_360) >= 0;
}) || [];
const prepareImages = (currentData, isMobileView, isTabletView, isThumbnails) => {
    const { url, label, id, type } = currentData;
    const dimension = getDimension({
        isMobileView,
        isTabletView,
        isThumbnails,
    });
    const src = getImageUrlByDimension({
        src: url,
        dimension,
    });
    const ImageComponent = (React.createElement(Image, { id: id, alt: label, src: src, isLazyLoadImage: true }));
    return {
        element: ImageComponent,
        id,
        type,
        elementType: GalleryItemType.IMAGE,
    };
};
const prepare360View = (imagesFor360, view360BtnRef) => {
    const { url: url360, width: width360, height: height360, label: label360, master, id, } = imagesFor360[0];
    const view360Item = (React.createElement(View360, { images: imagesFor360, key: url360, btnRef: view360BtnRef, "data-type": VIEW360 }));
    const view360Image = (React.createElement("img", { src: url360, width: width360, height: height360, key: url360, alt: label360, "data-type": VIEW360 }));
    const view360Thumbnail = view360Image;
    return {
        view360Item,
        view360Image,
        view360Thumbnail,
        master,
        id,
    };
};
export const prepareVideo = (youtubeId) => {
    const videoItem = (React.createElement(VideoPlayer, { videoPath: buildYoutubeUrl(youtubeId), isAutoStopAllowed: true, isHiddenForKeyboard: true, "data-type": VIDEO }));
    const thumbnailUrl = getYoutubePreview(youtubeId, true);
    const videoThumbnail = (React.createElement("img", { src: thumbnailUrl, alt: VIDEO, "data-type": VIDEO }));
    return {
        videoItem,
        videoThumbnail,
    };
};
const prepareImagesInRightOrder = (images, is360ImageViewed) => {
    let images360 = [];
    let imagesWithActionAndStaticType = [];
    let removeNextStudioImage = false;
    let studioImageShouldBeFirstElement = false;
    let existImage360 = false;
    return images.reduce((allImagesInRightOrder, imageItem, index) => {
        const isStudioImage = imageItem?.type === STUDIO_IMAGE;
        const isLastImageItem = images.length === index + 1;
        const isNextLastImageItem = images.length === index + 2;
        if (!is360ImageViewed && imageItem?.type === DEGREES_360) {
            return allImagesInRightOrder;
        }
        if (removeNextStudioImage && isStudioImage) {
            removeNextStudioImage = false;
            return allImagesInRightOrder;
        }
        if (studioImageShouldBeFirstElement && isStudioImage) {
            const actionAndStaticImagesForAdded = imagesWithActionAndStaticType;
            imagesWithActionAndStaticType = [];
            studioImageShouldBeFirstElement = false;
            if (existImage360) {
                existImage360 = false;
                return [...allImagesInRightOrder, ...actionAndStaticImagesForAdded, imageItem];
            }
            return [...allImagesInRightOrder, imageItem, ...actionAndStaticImagesForAdded];
        }
        if (imageItem?.type === ACTION_IMAGE || imageItem?.type === STATIC_IMAGE) {
            imagesWithActionAndStaticType.push(imageItem);
            if (isLastImageItem || images[index + 1]?.type === DEGREES_360) {
                return [...allImagesInRightOrder, ...imagesWithActionAndStaticType];
            }
            if (isNextLastImageItem && images[index + 1]?.type === STUDIO_IMAGE) {
                return [...allImagesInRightOrder, ...imagesWithActionAndStaticType];
            }
            if (images[index + 1]?.type === STUDIO_IMAGE && !images360.length) {
                studioImageShouldBeFirstElement = true;
            }
        }
        if (isStudioImage) {
            allImagesInRightOrder.push(imageItem);
        }
        if (imageItem?.type === DEGREES_360 && is360ImageViewed) {
            images360.push(imageItem);
            if (isLastImageItem) {
                return [...allImagesInRightOrder, images360];
            }
            if (images[index + 1]?.type !== DEGREES_360) {
                removeNextStudioImage = true;
                existImage360 = true;
                allImagesInRightOrder.push(images360);
                images360 = [];
            }
        }
        return allImagesInRightOrder;
    }, []);
};
const prepareAllImages = (images, isImage360, view360BtnRef, isMobileView, isTabletView, isThumbnails) => images.reduce((preparedImages, imageItem) => {
    if (Array.isArray(imageItem)) {
        const { view360Item, view360Image, id } = prepare360View(imageItem, view360BtnRef);
        if (isImage360) {
            preparedImages.push({
                element: view360Image,
                id,
                type: DEGREES_360,
            });
        }
        else {
            preparedImages.push({
                element: view360Item,
                id,
                type: DEGREES_360,
            });
        }
    }
    else {
        preparedImages.push(prepareImages(imageItem, isMobileView, isTabletView, Boolean(isThumbnails)));
    }
    return preparedImages;
}, []);
const filterUniqueUrls = (arr) => Array.isArray(arr) &&
    arr.reduce((acc, obj) => {
        const isItemExists = acc.some((item) => item.id === obj.id);
        if (!isItemExists) {
            acc.push(obj);
        }
        return acc;
    }, []);
function filterAccessorizedImages(images) {
    return images.filter((el) => !el.url.toLocaleLowerCase().includes(ACCESSORY_KEY));
}
// TODO-Astrouski need to refactor helper below (avoid duplicated code)
export const prepareGalleryImages = (rawFullImagesList = [], rawImages = [], youTubeIdList, view360BtnRef, is360ImageViewed, isMobileView, isTabletView) => {
    const fullImagesList = filterAccessorizedImages(rawFullImagesList);
    const images = filterAccessorizedImages(rawImages);
    const imagesFor360 = take360DegreesImages(images);
    const imagesFor360FromFullList = take360DegreesImages(fullImagesList);
    const preparedVariantElements = prepareImagesInRightOrder(images, is360ImageViewed);
    const preparedElements = prepareImagesInRightOrder(fullImagesList, is360ImageViewed);
    const youTubeIdReverseList = youTubeIdList.map((youTubeIdItem) => youTubeIdItem).reverse();
    // for gallery
    let galleryItems = prepareAllImages(preparedVariantElements, false, view360BtnRef, isMobileView, isTabletView);
    let galleryThumbnails = prepareAllImages(preparedVariantElements, true, view360BtnRef, isMobileView, isTabletView, true);
    // for lightbox
    const lightboxGenericItems = prepareAllImages(preparedElements, true, view360BtnRef, isMobileView, isTabletView);
    const lightboxGenericThumbnails = prepareAllImages(preparedElements, true, view360BtnRef, isMobileView, isTabletView, true);
    const lightboxVariantItems = prepareAllImages(preparedVariantElements, true, view360BtnRef, isMobileView, isTabletView);
    const lightboxVariantThumbnails = prepareAllImages(preparedVariantElements, true, view360BtnRef, isMobileView, isTabletView, true);
    let lightboxItems = filterUniqueUrls([...lightboxGenericItems, ...lightboxVariantItems]);
    let lightboxThumbnails = filterUniqueUrls([
        ...lightboxGenericThumbnails,
        ...lightboxVariantThumbnails,
    ]);
    if (youTubeIdReverseList.length) {
        youTubeIdReverseList.forEach((youTubeId) => {
            const { videoItem, videoThumbnail } = prepareVideo(youTubeId);
            galleryItems = [
                {
                    element: videoItem,
                    id: youTubeId,
                },
                ...galleryItems,
            ];
            lightboxItems = [
                {
                    element: videoItem,
                    id: youTubeId,
                },
                ...lightboxItems,
            ];
            galleryThumbnails = [
                {
                    element: videoThumbnail,
                    id: youTubeId,
                },
                ...galleryThumbnails,
            ];
            lightboxThumbnails = [
                {
                    element: videoThumbnail,
                    id: youTubeId,
                },
                ...lightboxThumbnails,
            ];
        });
    }
    if (!imagesFor360.length) {
        moveGeneralImageToStart([galleryItems, galleryThumbnails], STUDIO_IMAGE);
    }
    else {
        moveGeneralImageToStart([galleryItems, galleryThumbnails], DEGREES_360);
    }
    if (imagesFor360FromFullList.length) {
        const { master } = prepare360View(imagesFor360FromFullList, view360BtnRef);
        if (master) {
            moveGeneralImageToStart([lightboxItems, lightboxThumbnails], DEGREES_360);
        }
        else {
            moveGeneralImageToStart([lightboxItems, lightboxThumbnails], STUDIO_IMAGE);
        }
    }
    else {
        moveGeneralImageToStart([lightboxItems, lightboxThumbnails], STUDIO_IMAGE);
    }
    return {
        imagesList: galleryItems,
        lightboxImagesList: lightboxItems,
        thumbnailsList: galleryThumbnails,
        lightboxThumbnails,
    };
};
export const getElementRef = (isActiveItem, currentRef) => {
    if (isActiveItem) {
        return currentRef;
    }
    return null;
};
export const checkIsView360ButtonPressed = (buttonRef, event) => {
    const isBtnChild = buttonRef?.current?.contains(event?.target);
    const isBtn = buttonRef?.current === event?.target;
    return isBtnChild || isBtn;
};
export const getThumbnailType = (type, isLightboxOpened) => {
    switch (type) {
        case VIEW360:
            return !isLightboxOpened ? VIEW360 : '';
        case VIDEO:
            return VIDEO;
        default:
            return '';
    }
};
